/**
 * A record's timeline: what people wrote on it and what the system did,
 * newest first, with a composer at the top for a note, a task or a call.
 *
 * Used on a deal (in its drawer), on a company (its Timeline tab) and on a
 * lead (in its detail). The server merges the sources and pages them; this
 * only draws them and offers the writes the person may make. Who may edit,
 * delete or tick an item comes from the item itself (`can_edit`,
 * `can_complete`), so the screen never offers what the server would refuse.
 */
import { lazy, Suspense, useState, type ReactNode } from 'react';
import {
  ActionIcon, Alert, Anchor, Button, Checkbox, Group, Loader, Menu, Modal, SegmentedControl, Select,
  Stack, Text, TextInput, Textarea, ThemeIcon, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowBackUp, IconArrowRight, IconBolt, IconBriefcase, IconCheckbox, IconDots, IconGitMerge, IconInbox, IconLink,
  IconMail, IconNote, IconPencil, IconPhone, IconRefresh, IconRoute, IconTrash, IconTrophy, IconUnlink, IconUserCheck, IconUserEdit, IconUsers,
  IconX,
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCreateActivity, useDeleteActivity, useSetTaskDone, useTimeline, useUpdateActivity,
  type Activity, type ActivityKind, type ActivityObjectType, type TimelineItem, type TimelineItemKind,
  type TimelineLink,
} from '../../api/activities';
import { ApiError } from '../../api/client';
import { useUndoLink } from '../../api/people';
import { TOKENS } from '../../theme';
import { relativeTime } from '../../ui/format';
import { useVocab, type Vocab } from '../../vocab';
import { OwnerSelect, useCrmPermissions } from './crmShared';

// The lead's own detail opens from a link on a deal or company timeline. It
// also hosts a timeline, so it loads on demand rather than as a static cycle.
const LeadDetailModal = lazy(() =>
  import('../LeadDetailModal').then((m) => ({ default: m.LeadDetailModal })),
);

const ICONS: Record<TimelineItemKind, typeof IconNote> = {
  note: IconNote,
  task: IconCheckbox,
  call: IconPhone,
  meeting: IconUsers,
  arrived: IconInbox,
  routed: IconRoute,
  redistributed: IconRefresh,
  returned: IconArrowBackUp,
  email: IconMail,
  source_arrived: IconInbox,
  deal_created: IconBriefcase,
  stage_changed: IconArrowRight,
  deal_won: IconTrophy,
  deal_lost: IconX,
  linked: IconLink,
  merged: IconGitMerge,
  unlinked: IconUnlink,
  owner_changed: IconUserEdit,
  assigned: IconUserCheck,
  // Something a workflow did: a task made, an owner given, a webhook called.
  workflow: IconBolt,
};

const COLOURS: Partial<Record<TimelineItemKind, string>> = {
  note: 'chartAmber',
  task: 'chartBlue',
  call: 'chartViolet',
  meeting: 'chartCyan',
  deal_won: 'chartGreen',
  deal_lost: 'chartRed',
  returned: 'chartRed',
  merged: 'chartBlue',
  workflow: 'chartCyan',
};

const exact = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London',
});

/** "16 Oct 2026, 14:30", UK time. */
export function exactTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : exact.format(d);
}

const dueFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
});

/** "Due Fri 16 Oct, 14:30", UK time. */
export function dueText(iso: string | null | undefined): string {
  if (!iso) return 'No due date';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `Due ${dueFormat.format(d)}`;
}

/** The words for an item kind a person wrote. */
export function kindWord(v: Vocab, kind: ActivityKind): string {
  return v.One(kind);
}

/** A `datetime-local` value ("2026-10-16T14:30") moved by some minutes, as the same kind of value. */
function shiftLocal(value: string, minutes: number): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  d.setMinutes(d.getMinutes() + minutes);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const OUTCOMES = ['Connected', 'No answer', 'Voicemail', 'Wrong number'];
const REMINDERS = [
  { value: '0', label: 'At the due time' },
  { value: '60', label: '1 hour before' },
  { value: '1440', label: '1 day before' },
];

type Mode = 'note' | 'task' | 'call';
type LinkInput = { object_type: ActivityObjectType; object_id: string };

/**
 * Write a note, set a task or log a call on this record (and on `extraLinks`,
 * a deal's company say). `canWrite` says whether the person may write here at
 * all; owners other than themselves need edit_activities.
 */
export function Composer({
  links, assignOthers, onDone,
}: { links: LinkInput[]; assignOthers: boolean; onDone?: () => void }) {
  const v = useVocab();
  const create = useCreateActivity();
  const me = useCrmPermissions().user?.id ?? null;
  const [mode, setMode] = useState<Mode>('note');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [due, setDue] = useState('');
  const [owner, setOwner] = useState<string | null>(me);
  const [reminder, setReminder] = useState<string>('0');
  const [outcome, setOutcome] = useState<string | null>(null);
  const [otherOutcome, setOtherOutcome] = useState('');
  const [error, setError] = useState('');

  function reset() {
    setSubject('');
    setBody('');
    setDue('');
    setOutcome(null);
    setOtherOutcome('');
    setReminder('0');
    setOwner(me);
    setError('');
  }

  async function save() {
    setError('');
    const callOutcome = outcome === 'Other' ? otherOutcome.trim() : outcome ?? '';
    if (mode === 'note' && !body.trim() && !subject.trim()) {
      setError('Write something first.');
      return;
    }
    if (mode === 'task' && !subject.trim()) {
      setError(`${v.A('task')} needs a subject.`);
      return;
    }
    if (mode === 'call' && !callOutcome && !body.trim()) {
      setError('Say how the call went, or add a note.');
      return;
    }
    try {
      await create.mutateAsync({
        kind: mode,
        subject: subject.trim(),
        body: body.trim(),
        links,
        ...(mode === 'task'
          ? {
              owner_user_id: owner ?? '',
              ...(due ? { due_at: due, reminder_at: shiftLocal(due, -Number(reminder)) } : {}),
            }
          : {}),
        ...(mode === 'call' && callOutcome ? { outcome: callOutcome } : {}),
      });
      notifications.show({
        color: 'teal',
        title: mode === 'task' ? `${v.One('task')} added` : mode === 'call' ? `${v.One('call')} logged` : `${v.One('note')} added`,
        message: subject.trim() || body.trim().split('\n')[0] || callOutcome,
      });
      reset();
      onDone?.();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Stack gap="xs" data-testid="composer">
      <SegmentedControl
        size="xs"
        value={mode}
        onChange={(m) => { setMode(m as Mode); setError(''); }}
        data={[
          { value: 'note', label: v.One('note') },
          { value: 'task', label: v.One('task') },
          { value: 'call', label: `Log ${v.one('call')}` },
        ]}
        style={{ alignSelf: 'flex-start' }}
      />
      {mode !== 'call' && (
        <TextInput
          size="sm"
          placeholder={mode === 'task' ? 'What needs doing' : 'Subject (optional)'}
          aria-label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
        />
      )}
      {mode === 'task' && (
        <Group grow align="flex-start" wrap="wrap" gap="xs">
          <TextInput
            size="sm"
            type="datetime-local"
            label="Due"
            value={due}
            onChange={(e) => setDue(e.currentTarget.value)}
            miw={180}
          />
          <Select
            size="sm"
            label="Reminder"
            data={REMINDERS}
            value={reminder}
            onChange={(r) => setReminder(r ?? '0')}
            allowDeselect={false}
            disabled={!due}
            miw={150}
          />
          {assignOthers ? (
            <OwnerSelect size="sm" label="Owner" value={owner} onChange={setOwner} miw={160} />
          ) : null}
        </Group>
      )}
      {mode === 'call' && (
        <Group grow align="flex-start" wrap="wrap" gap="xs">
          <Select
            size="sm"
            label="Outcome"
            placeholder="How did it go?"
            data={[...OUTCOMES, 'Other']}
            value={outcome}
            onChange={setOutcome}
            miw={160}
          />
          {outcome === 'Other' && (
            <TextInput
              size="sm"
              label="Outcome"
              value={otherOutcome}
              onChange={(e) => setOtherOutcome(e.currentTarget.value)}
              miw={160}
            />
          )}
        </Group>
      )}
      {mode !== 'task' && (
        <Textarea
          size="sm"
          autosize
          minRows={2}
          maxRows={8}
          placeholder={mode === 'call' ? 'What was said' : `Write ${v.a('note')}`}
          aria-label={`${v.One('note')} text`}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
        />
      )}
      {error && <Text size="xs" style={{ color: TOKENS.redText }}>{error}</Text>}
      <Group justify="flex-end">
        <Button size="xs" onClick={save} loading={create.isPending}>
          {mode === 'task' ? `Add ${v.one('task')}` : mode === 'call' ? `Log ${v.one('call')}` : `Add ${v.one('note')}`}
        </Button>
      </Group>
    </Stack>
  );
}

/** Edit a note, task or call: its words, and a task's due time. */
function EditModal({ activity, onClose }: { activity: Activity | null; onClose: () => void }) {
  const v = useVocab();
  const update = useUpdateActivity();
  const [subject, setSubject] = useState(activity?.subject ?? '');
  const [body, setBody] = useState(activity?.body ?? '');
  const [due, setDue] = useState(() => toLocalInput(activity?.due_at));
  const [error, setError] = useState('');
  if (!activity) return null;

  async function save() {
    if (!activity) return;
    setError('');
    try {
      await update.mutateAsync({
        id: activity.id,
        body: {
          subject: subject.trim(),
          body: body.trim(),
          ...(activity.kind === 'task' && due !== toLocalInput(activity.due_at) ? { due_at: due } : {}),
        },
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal opened onClose={onClose} title={`Edit ${v.one(activity.kind)}`} size="md">
      <Stack gap="sm">
        <TextInput label="Subject" value={subject} onChange={(e) => setSubject(e.currentTarget.value)} />
        {activity.kind === 'task' && (
          <TextInput type="datetime-local" label="Due" value={due} onChange={(e) => setDue(e.currentTarget.value)} />
        )}
        <Textarea label={v.One('note')} autosize minRows={3} value={body} onChange={(e) => setBody(e.currentTarget.value)} />
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={update.isPending}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** A UTC time as a `datetime-local` value in this browser's time. */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The tick box on a task, wherever it is shown. */
export function TaskCheck({ activity, size = 'sm' }: { activity: Activity; size?: 'xs' | 'sm' | 'md' }) {
  const v = useVocab();
  const setDone = useSetTaskDone();
  const done = activity.status === 'done';
  return (
    <Checkbox
      size={size}
      radius="xl"
      checked={done}
      disabled={!activity.can_complete || setDone.isPending}
      aria-label={done ? `Reopen ${v.one('task')}: ${activity.subject}` : `Complete ${v.one('task')}: ${activity.subject}`}
      onChange={async () => {
        try {
          await setDone.mutateAsync({ id: activity.id, done: !done });
        } catch (e) {
          notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message });
        }
      }}
    />
  );
}

/** A link from an item to another record. */
function LinkTo({ link, onLead }: { link: TimelineLink; onLead: (id: string) => void }) {
  const v = useVocab();
  const navigate = useNavigate();
  // A page this person cannot open (CRM switched off, or no tab for it) is
  // named but not linked, rather than a link that bounces to Home.
  const perms = useCrmPermissions();
  const a = (label: string, go: () => void) => (
    <Anchor size="xs" underline="always" fw={500} style={{ color: TOKENS.textSecondary }} onClick={go}>{label}</Anchor>
  );
  const plain = (label: string) => <Text span size="xs" fw={500} style={{ color: TOKENS.textSecondary }}>{label}</Text>;
  switch (link.object_type) {
    case 'deal':
      if (!perms.seesDeals) return plain(link.label ?? v.One('deal'));
      return a(link.label ?? `Open ${v.one('deal')}`, () => navigate(`/deals?deal=${encodeURIComponent(link.object_id)}`));
    case 'company':
      if (!perms.seesCompanies) return plain(link.label ?? v.One('company'));
      return a(link.label ?? `Open ${v.one('company')}`, () => navigate(`/companies/${encodeURIComponent(link.object_id)}`));
    case 'submission':
      return a(link.label ?? `Open the ${v.one('lead')}`, () => onLead(link.object_id));
    case 'person':
      return a(link.label ?? `Open the ${v.one('person')}`, () => navigate(`/people/${encodeURIComponent(link.object_id)}`));
    case 'partner_leads':
    case 'source_leads': {
      const key = link.object_type === 'partner_leads' ? 'buyer_ids' : 'supplier_ids';
      return a(
        link.label ?? `Open ${v.many('lead')}`,
        () => navigate('/leads', { state: { cmdk: { filters: { [key]: [link.object_id] }, search: '' } } }),
      );
    }
  }
}

function Item({
  item, onEdit, onLead, canUndo,
}: { item: TimelineItem; onEdit: (a: Activity) => void; onLead: (id: string) => void; canUndo: boolean }) {
  const v = useVocab();
  const remove = useDeleteActivity();
  const undo = useUndoLink();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  async function undoLink() {
    if (!item.undo_link_id) return;
    try {
      const r = await undo.mutateAsync(item.undo_link_id);
      notifications.show({ color: 'teal', title: 'Undone', message: item.text });
      // The person on screen may be the one the undo took apart.
      if (pathname.startsWith('/people/')) navigate(`/people/${encodeURIComponent(r.person_id)}`);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not undone', message: (e as Error).message });
    }
  }
  const a = item.activity ?? null;
  const Icon = ICONS[item.kind] ?? IconNote;
  const [open, setOpen] = useState(false);
  // A workflow's line already says what it did, which is all its detail holds.
  const detail = item.kind === 'workflow' ? '' : (item.detail ?? '');
  const long = detail.length > 220 || detail.split('\n').length > 3;

  async function del() {
    if (!a) return;
    if (!window.confirm(`Delete this ${v.one(a.kind)}?`)) return;
    try {
      await remove.mutateAsync(a.id);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message });
    }
  }

  return (
    <Group align="flex-start" gap="sm" wrap="nowrap" data-testid={`timeline-item-${item.kind}`}>
      <ThemeIcon variant="light" radius="xl" size={28} color={COLOURS[item.kind] ?? 'gray'} style={{ flex: 'none' }}>
        <Icon size={15} />
      </ThemeIcon>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} wrap="nowrap" align="flex-start" justify="space-between">
          <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
            {a?.kind === 'task' && <TaskCheck activity={a} size="xs" />}
            <Text
              size="sm"
              fw={a ? 500 : 400}
              style={{
                color: TOKENS.text,
                textDecoration: a?.kind === 'task' && a.status === 'done' ? 'line-through' : undefined,
                overflowWrap: 'anywhere',
              }}
            >
              {a && a.kind !== 'note' && <Text span size="xs" c="dimmed" mr={6}>{kindWord(v, a.kind)}</Text>}
              {item.text}
            </Text>
          </Group>
          {a && a.can_edit && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`${v.One(a.kind)} actions`}>
                  <IconDots size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => onEdit(a)}>Edit</Menu.Item>
                <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={del}>Delete</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
        {a?.kind === 'task' && (
          <Text size="xs" style={{ color: a.overdue ? TOKENS.redText : TOKENS.textMuted }}>
            {a.status === 'done' ? `Done ${relativeTime(a.completed_at ?? '')}` : dueText(a.due_at)}
            {a.owner_name ? ` · ${a.owner_name}` : ''}
          </Text>
        )}
        {a?.kind === 'call' && a.outcome && item.text !== a.outcome && (
          <Text size="xs" c="dimmed">{a.outcome}</Text>
        )}
        {detail && (
          <Text
            size="sm"
            mt={2}
            lineClamp={long && !open ? 3 : undefined}
            style={{ whiteSpace: 'pre-wrap', color: a ? TOKENS.text : TOKENS.textSecondary, overflowWrap: 'anywhere' }}
          >
            {detail}
          </Text>
        )}
        {long && (
          <Anchor size="xs" onClick={() => setOpen(!open)}>{open ? 'Show less' : 'Show more'}</Anchor>
        )}
        <Group gap={6} mt={2} wrap="wrap">
          <Tooltip label={exactTime(item.at)} withArrow openDelay={300}>
            <Text size="xs" c="dimmed" component="time" dateTime={item.at}>{relativeTime(item.at)}</Text>
          </Tooltip>
          {item.by_name && <Text size="xs" c="dimmed">{`· ${item.by_name}`}</Text>}
          {item.link && <><Text size="xs" c="dimmed">·</Text><LinkTo link={item.link} onLead={onLead} /></>}
          {canUndo && item.undo_link_id && (
            <>
              <Text size="xs" c="dimmed">·</Text>
              <Anchor
                size="xs"
                underline="always"
                fw={500}
                style={{ color: TOKENS.textSecondary }}
                onClick={undoLink}
                aria-disabled={undo.isPending}
              >
                Undo
              </Anchor>
            </>
          )}
        </Group>
      </div>
    </Group>
  );
}

/**
 * The timeline for one record. `extraLinks` are records every new item also
 * sits on (a deal's company). `header` goes above the composer.
 */
export function TimelinePanel({
  objectType, objectId, extraLinks = [], header,
}: {
  objectType: ActivityObjectType;
  objectId: string;
  extraLinks?: LinkInput[];
  header?: ReactNode;
}) {
  const v = useVocab();
  const perms = useCrmPermissions();
  const q = useTimeline(objectType, objectId);
  const [editing, setEditing] = useState<Activity | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  // Anyone who can read leads may write on one, or on the person they came
  // from; anything else needs the permission.
  const canWrite = perms.editActivities
    || (objectType === 'submission' && perms.seesTimeline)
    || (objectType === 'person' && perms.seesPeople);

  if (q.error) {
    const forbidden = q.error instanceof ApiError && q.error.status === 403;
    return (
      <Text size="sm" c="dimmed">
        {forbidden ? 'Timelines are not available to this login.' : q.error.message}
      </Text>
    );
  }
  const pages = q.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const summaries = pages[0]?.summaries ?? [];
  const notes = pages[0]?.notes ?? [];

  return (
    <Stack gap="md" data-testid="timeline">
      {header}
      {canWrite && (
        <Composer
          links={[{ object_type: objectType, object_id: objectId }, ...extraLinks]}
          assignOthers={perms.editActivities}
        />
      )}
      {summaries.map((s) => (
        <Group
          key={s.link.object_id + s.link.object_type}
          gap={8}
          wrap="wrap"
          p="xs"
          style={{ background: TOKENS.surface2, borderRadius: 6 }}
        >
          <Text size="xs" c="dimmed">{s.text}</Text>
          <LinkTo link={s.link} onLead={setLeadId} />
        </Group>
      ))}
      {notes.map((n) => <Text key={n} size="xs" c="dimmed">{n}</Text>)}
      {q.isPending && <Group justify="center" p="md"><Loader size="sm" /></Group>}
      {!q.isPending && items.length === 0 && (
        <Text size="sm" c="dimmed">{`Nothing yet. ${canWrite ? `Add ${v.a('note')} or ${v.a('task')} above.` : ''}`.trim()}</Text>
      )}
      <Stack gap="md">
        {items.map((i) => (
          <Item key={i.id} item={i} onEdit={setEditing} onLead={setLeadId} canUndo={perms.linkPeople} />
        ))}
      </Stack>
      {q.hasNextPage && (
        <Button variant="subtle" size="xs" onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>
          Show older
        </Button>
      )}
      {editing && <EditModal key={editing.id} activity={editing} onClose={() => setEditing(null)} />}
      {leadId && objectType !== 'submission' && (
        <Suspense fallback={null}>
          <LeadDetailModal id={leadId} onClose={() => setLeadId(null)} />
        </Suspense>
      )}
    </Stack>
  );
}
